import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { handleApiErrors } from '../../../api/axiosInstance';
import { fetchVehicleInsuranceUploadedFilePreview } from '../../../api/vehicle-insurances';
import { setFullscreenMaxHeight, setModalFullscreenWidth } from '../../../utils/common';
import SpaceSpinner from '../../SpaceSpinner';
import Toast from '../../Toast';
import GenericModal from '../GenericModal';
import styles from './styles.module.scss';

const UploadedFilePreviewModal = ({
  t,
  visible,
  onCancel,
  vehicleInsuranceId,
  uploadedFileId,
  filename,
}) => {
  const [documentPreview, setDocumentPreview] = useState();

  const documentPreviewQuery = useQuery({
    enabled: visible && !!vehicleInsuranceId && !!uploadedFileId,
    queryKey: [
      'fetchVehicleInsuranceDocumentPreview',
      vehicleInsuranceId,
      uploadedFileId,
      filename,
    ],
    queryFn: () =>
      fetchVehicleInsuranceUploadedFilePreview(vehicleInsuranceId, uploadedFileId, filename),
    onSuccess: setDocumentPreview,
    onError: error => {
      handleApiErrors(error.response, () => {
        Toast({
          type: 'error',
          message: t('fetchInsuranceDocumentPreviewError'),
        });
      });
      onCancel();
    },
  });

  const clearDocumentPreview = () => {
    if (documentPreview?.url) {
      URL.revokeObjectURL(documentPreview.url);
    }
    setDocumentPreview();
  };

  useEffect(
    () => () => {
      if (documentPreview?.url) {
        URL.revokeObjectURL(documentPreview.url);
      }
    },
    [documentPreview],
  );

  const isImagePreview = documentPreview?.type?.startsWith('image/');
  const hasImageDimensions = documentPreview?.width > 0 && documentPreview?.height > 0;
  const previewHeight = setFullscreenMaxHeight(1400);
  const previewWidth =
    isImagePreview && hasImageDimensions
      ? Math.min(
          setModalFullscreenWidth(),
          Math.max(400, previewHeight * (documentPreview.width / documentPreview.height)),
        )
      : setModalFullscreenWidth();

  return (
    <GenericModal
      centered
      className={styles.uploadedFilePreviewModal}
      footer={null}
      bodyStyle={{ height: '100%' }}
      width={previewWidth}
      height={previewHeight}
      title={t('insuranceDocumentPreview')}
      visible={visible}
      onCancel={onCancel}
      afterClose={clearDocumentPreview}
    >
      {documentPreviewQuery.isFetching && <SpaceSpinner />}

      {documentPreview && !documentPreviewQuery.isFetching && (
        <div className={styles.previewContainer}>
          {isImagePreview ? (
            <img
              alt={t('insuranceDocumentPreview')}
              src={documentPreview.url}
              className={styles.imagePreview}
            />
          ) : (
            <iframe
              frameBorder="0"
              height="99%"
              width="100%"
              title="insurance document"
              src={documentPreview.url}
            />
          )}
        </div>
      )}
    </GenericModal>
  );
};

UploadedFilePreviewModal.defaultProps = {
  filename: undefined,
  onCancel: () => undefined,
  t: key => key,
  uploadedFileId: undefined,
  vehicleInsuranceId: undefined,
  visible: false,
};

export default UploadedFilePreviewModal;
